/**
 * Where each claims extraction in ds-docmessage puts the claim, and the key its id sits under.
 *
 * <p>Taken from the routes that read it back (`EmailProcessingService`): a claims ops email
 * (vendor invoices, claim inquiries) under `claimsOps.claimDetails`, an adjuster's acknowledgment
 * under `claims`, and a patient-zero email under `patientZero`. An FNOL intake has no number yet:
 * the claim it opens is created after extraction, and nothing writes the number back.
 *
 * <p>The id is there only when enrichment resolved the claim, so the number is what decides
 * whether an email names a claim at all.
 */
const CLAIM_LOCATIONS: ReadonlyArray<{
  readonly parent: ReadonlyArray<string>;
  readonly idKey: string;
}> = [
  { parent: ['claimsOps', 'claimDetails'], idKey: 'claimId' },
  { parent: ['claims'], idKey: 'id' },
  { parent: ['patientZero'], idKey: 'id' },
];

/** The claim an email's extraction named, and its id when enrichment resolved one. */
export interface ExtractedClaim {
  readonly claimNumber: string;
  readonly claimId: string | null;
}

/**
 * A field as the extractor writes it: either the bare value or `{ value, confidence }`, the same
 * two shapes `getJsonValueWithConfidence` accepts on the backend.
 */
const readValue = (field: unknown): string | null => {
  let value: unknown = field;
  if (value !== null && typeof value === 'object') {
    value = (value as { value?: unknown }).value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const readPath = (root: unknown, path: ReadonlyArray<string>): unknown =>
  path.reduce<unknown>((node, key) => {
    if (node === null || typeof node !== 'object') {
      return undefined;
    }
    return (node as Record<string, unknown>)[key];
  }, root);

/**
 * The claim an email's extraction identified, or null when it identified none or the extraction
 * is missing or not JSON.
 */
export const extractClaim = (extractedData?: string): ExtractedClaim | null => {
  if (!extractedData) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(extractedData);
  } catch {
    return null;
  }
  for (const { parent, idKey } of CLAIM_LOCATIONS) {
    const node = readPath(parsed, parent);
    const claimNumber = readValue(readPath(node, ['claimNumber']));
    if (claimNumber) {
      return { claimNumber, claimId: readValue(readPath(node, [idKey])) };
    }
  }
  return null;
};
